import 'react-datepicker/dist/react-datepicker.css';
import './app-fonts';

import { AuthProvider, RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';
import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { handleApiErrorMessage, isAxiosError, staticErrorMessages } from 'apis/api';
import { Provider } from 'components/ui/provider';
import { Toaster, toaster } from 'components/ui/toaster';
import useIsEmbeddedInIframe from 'hooks/useIsEmbeddedInIframe';
import { WithTrpcWithPropelAuth } from 'hooks/useTrpcWithPropelAuth';
import { StrictMode } from 'react';

import { AppContent } from './app-content';

/**
 * A helper for showing error logs and toast notifications.
 */
function handleGlobalError(error: any) {
  if (isAxiosError(error)) {
    const errorMessage = handleApiErrorMessage(error);
    const errorStatus = error?.status as keyof typeof staticErrorMessages;
    const errorMsgTitle = errorMessage?.title ?? `Request failed with status code ${errorStatus}`;
    const errorMsgDescription = errorMessage?.message || staticErrorMessages[errorStatus];

    // Capture 400-level errors with Sentry
    if (errorStatus >= 400 && errorStatus < 500) {
      Sentry.captureException(error, {
        extra: {
          status: errorStatus,
          title: errorMsgTitle,
          description: errorMsgDescription,
        },
      });
    }

    toaster.create({
      type: 'error',
      title: errorMsgTitle,
      description: errorMsgDescription,
    });
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: failureCount => failureCount < 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      networkMode: 'online',
      refetchOnMount: false,
    },
    mutations: {
      retry: failureCount => failureCount < 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      networkMode: 'online',
      onError: (error: any) => handleGlobalError(error),
    },
  },
  queryCache: new QueryCache({
    onError: (error: any) => handleGlobalError(error),
  }),
});

const App = () => {
  const isEmbeddedInIframe = useIsEmbeddedInIframe();

  // If the app is embedded in an iframe, this means the app is opened in Shopify
  // Then, we will open it in a new tab for authentication if needed
  // ToDo: we should open new tab only when the user is not authenticated
  const AuthProviderWrapper = isEmbeddedInIframe ? AuthProvider : RequiredAuthProvider;
  return (
    <StrictMode>
      <Provider>
        <QueryClientProvider client={queryClient}>
          <AuthProviderWrapper
            authUrl={import.meta.env.VITE_AUTH_URL}
            displayIfLoggedOut={<RedirectToLogin postLoginRedirectUrl={window.location.href} />}
          >
            <Toaster />
            <WithTrpcWithPropelAuth />
            <AppContent isEmbeddedInIframe={isEmbeddedInIframe} />
          </AuthProviderWrapper>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Provider>
    </StrictMode>
  );
};

export default App;
